/**
 * Downloads Block Style Generation
 *
 * Contains all styling logic for the Downloads block
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import {
	BackgroundAttributes,
	ButtonAttributes,
	getBackgroundCSS,
	getButtonCSS,
	getSpacingCSS,
	getTypographyCSS,
} from '@redfox-thank-you/control-groups';
import { createAttributeManager } from '@redfox-thank-you/utils';
import { applyFilters } from '@wordpress/hooks';

import type { DownloadsAttributes } from './types';

/**
 * Generate CSS styles for the Downloads block
 *
 * @param attributes - Block attributes containing style and settings data
 * @param blockClass - Unique CSS class for the block instance
 * @returns CSS string with all block styles
 */
export function generateDownloadsCSS(
	attributes: DownloadsAttributes,
	blockClass: string,
): string {
	const css: string[] = [];
	const blockSelector = `.${ blockClass }`;

	// Create attribute manager to safely access nested attributes
	const { getAttribute } = createAttributeManager(
		attributes as Record< string, unknown >,
		() => {},
	);

	// Extract style properties with defaults using getAttribute
	const blockTextStyle = getAttribute( 'style.block.text', {} );
	const blockBackgroundStyle = getAttribute(
		'style.block.background',
		{},
	) as BackgroundAttributes;
	const blockSpacingStyle = getAttribute( 'style.block.spacing', {} );
	const titleStyle = getAttribute( 'style.title', {} );
	const itemsStyle = getAttribute( 'style.items', {} );
	const downloadButtonStyle = getAttribute(
		'style.downloadButton',
		{},
	) as ButtonAttributes;

	// Extract settings properties with defaults using getAttribute
	const showTitle = getAttribute< boolean >( 'settings.title.show', true );
	const showIcons = getAttribute< boolean >( 'settings.display.icons', true );
	const showDownloadCount = getAttribute< boolean >(
		'settings.display.downloadCount',
		true,
	);

	// Title styles
	if ( showTitle ) {
		const titleSelector = `${ blockSelector } .redfox-thank-you-downloads__title`;

		// Use typography style function for title typography
		const titleTypographyCSS = getTypographyCSS(
			titleStyle,
			titleSelector,
		);
		if ( titleTypographyCSS ) {
			css.push( titleTypographyCSS );
		}
	}

	// Block text styles
	const blockTextTypographyCSS = getTypographyCSS(
		blockTextStyle,
		blockSelector,
	);
	if ( blockTextTypographyCSS ) {
		css.push( blockTextTypographyCSS );
	}

	// Items title styles - use TypographyControl
	const itemsSelector = `${ blockSelector } .redfox-thank-you-downloads__item-name`;
	const itemsTypographyCSS = getTypographyCSS( itemsStyle, itemsSelector );
	if ( itemsTypographyCSS ) {
		css.push( itemsTypographyCSS );
	}

	// Download button styles
	const downloadButtonSelector = `${ blockSelector } .redfox-thank-you-downloads__download-btn`;
	const downloadButtonCSS = getButtonCSS(
		downloadButtonStyle,
		downloadButtonSelector,
	);
	if ( downloadButtonCSS ) {
		css.push( downloadButtonCSS );
	}

	// Background styles - responsive
	const backgroundCSS = getBackgroundCSS(
		blockBackgroundStyle,
		blockSelector,
	);
	if ( backgroundCSS ) {
		css.push( backgroundCSS );
	}

	// Spacing styles - Generate for desktop, tablet, and mobile
	const spacingStyles = getSpacingCSS( blockSpacingStyle, blockSelector );

	if ( spacingStyles ) {
		css.push( spacingStyles );
	}

	// Add overlay support styles - responsive aware
	const hasOverlay = [ 'desktop', 'tablet', 'mobile' ].some(
		( breakpoint ) => {
			const breakpointKey =
				breakpoint as keyof typeof blockBackgroundStyle;
			const breakpointValues = blockBackgroundStyle[ breakpointKey ];
			return breakpointValues?.backgroundOverlay?.enabled;
		},
	);

	if ( hasOverlay ) {
		css.push( `
			${ blockSelector } {
				position: relative;
			}
			${ blockSelector } .redfox-thank-you-downloads__title,
			${ blockSelector } .redfox-thank-you-downloads__content {
				position: relative;
				z-index: 2;
			}
		` );
	}

	// Icon visibility styles
	if ( ! showIcons ) {
		css.push(
			`${ blockSelector } .redfox-thank-you-downloads__icon { display: none; }`,
		);
	}

	// Download count visibility styles
	if ( ! showDownloadCount ) {
		css.push(
			`${ blockSelector } .redfox-thank-you-downloads__count { display: none; }`,
		);
	}

	/**
	 * Filter to extend block CSS generation.
	 * Pro plugins can use this to add additional CSS rules (e.g., border, box-shadow).
	 *
	 * @param {string} css - Current CSS string
	 * @param {Object} props - Props containing attributes and selector
	 * @param {Object} props.attributes - Block attributes
	 * @param {string} props.blockSelector - CSS selector for the block
	 * @param {string} props.blockName - Block name identifier
	 */
	const extendedCSS = applyFilters(
		'redfox_thank_you.block.css',
		css.join( '\n' ),
		{
			attributes,
			blockSelector,
			blockName: 'redfox-thank-you/downloads',
		},
	) as string;

	return extendedCSS;
}
